import React from "react";
import { Avatar, Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import { COLORS } from "components/v3/Theme/colors";
import Translate from "translate/Translate";

export type AccountManagementMenuRowProps = {
  id: string;
  label: string;
  icon: React.ReactNode;
  to: string;
  showIndicator?: boolean;
};

const AccountManagementMenuRow: React.FC<AccountManagementMenuRowProps> = ({
  id,
  label,
  icon,
  to,
  showIndicator = false
}) => {
  const history = useHistory();

  return (
    <Box
      id={id}
      component="button"
      type="button"
      onClick={() => history.push(to, { account: true, redirectState: "" })}
      sx={{
        alignItems: "center",
        backgroundColor: COLORS.PRIMARY_LIGHTEST,
        border: "none",
        borderRadius: "12px",
        cursor: "pointer",
        display: "flex",
        gap: 2,
        paddingX: 2.5,
        paddingY: 2,
        textAlign: "left",
        width: "100%",
        transition: "background-color 0.15s ease",
        "&:hover": {
          backgroundColor: COLORS.PRIMARY_LIGHTER
        },
        "&:focus-visible": {
          outline: `2px solid ${COLORS.ACCENT2}`,
          outlineOffset: 2
        }
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flex: 1,
          gap: 2,
          minWidth: 0
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            color: COLORS.PRIMARY,
            display: "flex",
            flexShrink: 0,
            height: 19,
            justifyContent: "center",
            width: 19,
            "& .MuiSvgIcon-root": {
              fontSize: 19
            },
            "& img": {
              display: "block",
              maxHeight: 19,
              maxWidth: 19,
              height: "auto",
              width: "auto",
              objectFit: "contain"
            }
          }}
        >
          {icon}
        </Box>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flex: 1,
            gap: 2,
            minWidth: 0
          }}
        >
          <Typography
            variant="body_strong_black"
            sx={{
              color: COLORS.PRIMARY,
              flex: showIndicator ? "0 1 auto" : 1,
              fontSize: "1rem",
              fontWeight: 600,
              minWidth: 0,
              ...(showIndicator
                ? {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }
                : {})
            }}
          >
            <Translate text={label} />
          </Typography>
          {showIndicator ? (
            <Avatar
              aria-hidden
              sx={{
                animation:
                  "accountManagementIndicatorPulse 3s infinite ease-in-out",
                bgcolor: alpha(COLORS.ACCENT4, 0.25),
                flexShrink: 0,
                fontSize: 12,
                height: 12,
                overflow: "visible",
                width: 12,
                "@keyframes accountManagementIndicatorPulse": {
                  "0%, 100%": {
                    boxShadow: "0 0 0 0"
                  },
                  "50%": {
                    boxShadow: `0 0 0 4px ${alpha(COLORS.ACCENT4, 0.45)}`
                  }
                }
              }}
            >
              <Box
                sx={{
                  bgcolor: COLORS.ACCENT4,
                  borderRadius: "50%",
                  height: 10,
                  width: 10
                }}
              />
            </Avatar>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default AccountManagementMenuRow;
